import { useState, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Icon } from '../common'
import './ImportDropzone.css'

/**
 * ImportDropzone - Drag and drop file upload component
 */
export default function ImportDropzone({ onFilesSelected, accept = '.json,.md' }) {
  const [isDragging, setIsDragging] = useState(false)
  const [dragCounter, setDragCounter] = useState(0)
  const inputRef = useRef(null)

  const handleDragEnter = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragCounter(prev => prev + 1)
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true)
    }
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragCounter(prev => {
      const newCount = prev - 1
      if (newCount === 0) {
        setIsDragging(false)
      }
      return newCount
    })
  }, [])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    setDragCounter(0)

    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      onFilesSelected(files)
    }
  }, [onFilesSelected])

  const handleClick = () => {
    inputRef.current?.click()
  }

  const handleInputChange = (e) => {
    const files = Array.from(e.target.files)
    if (files.length > 0) {
      onFilesSelected(files)
    }
    // Reset input so same file can be selected again
    e.target.value = ''
  }

  return (
    <motion.div
      className={`import-dropzone ${isDragging ? 'import-dropzone--dragging' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={handleClick}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
    >
      <input
        ref={inputRef}
        type="file"
        className="import-dropzone__input"
        accept={accept}
        multiple
        onChange={handleInputChange}
      />
      
      <div className="import-dropzone__content">
        <AnimatePresence mode="wait">
          {isDragging ? (
            <motion.div
              key="dragging"
              className="import-dropzone__icon-container"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
            >
              <div className="import-dropzone__icon import-dropzone__icon--active">
                <Icon name="import" size={20} />
              </div>
              <p className="import-dropzone__text">Drop files here</p>
            </motion.div>
          ) : (
            <motion.div
              key="idle"
              className="import-dropzone__icon-container"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
            >
              <div className="import-dropzone__icon">
                <Icon name="import" size={48} />
              </div>
              <p className="import-dropzone__text">
                Drop files here or click to browse
              </p>
              <p className="import-dropzone__subtext">
                Accepts .json and .md files
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Animated border */}
      <div className="import-dropzone__border" />
    </motion.div>
  )
}

